// SAD-491 (SHAPE-03): TanStack Query bindings for Customer Themes over zou's
// ProjectDataService. ListCustomerThemes returns identity/lifecycle only —
// substance (title, narrative, ...) lives in versions, so a theme card reads
// it via GetCustomerTheme's aggregate (theme + versions + evidence).
import { Timestamp } from '@bufbuild/protobuf';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { projectDataClient } from '../../lib/connect/clients';
function toTs(d) {
    return d ? Timestamp.fromDate(d) : undefined;
}
const CUSTOMER_THEMES_KEY = 'customer-themes';
const CUSTOMER_THEME_KEY = 'customer-theme';
// correlationHeader threads one workspace correlation across a session's
// mutations (zou reads X-Workspace-Correlation to group the audit timeline).
function correlationHeader(id) {
    return id ? {
        headers: {
            'X-Workspace-Correlation': id
        }
    } : undefined;
}
/** List a pursuit's customer themes (identity + lifecycle, no substance). */ export function useCustomerThemes(captureProjectId) {
    return useQuery({
        queryKey: [
            CUSTOMER_THEMES_KEY,
            captureProjectId
        ],
        enabled: !!captureProjectId,
        queryFn: async ()=>{
            const resp = await projectDataClient.listCustomerThemes({
                captureProjectId
            });
            return resp.themes;
        }
    });
}
/** One theme's detail aggregate: the theme plus its full version history and evidence. */ export function useCustomerTheme(captureProjectId, themeId) {
    return useQuery({
        queryKey: [
            CUSTOMER_THEME_KEY,
            captureProjectId,
            themeId
        ],
        enabled: !!captureProjectId && !!themeId,
        queryFn: async ()=>{
            return projectDataClient.getCustomerTheme({
                captureProjectId,
                themeId
            });
        }
    });
}
function versionInput(args) {
    return {
        title: args.title,
        narrative: args.narrative ?? '',
        whyEvidenceSupports: args.whyEvidenceSupports ?? '',
        whyItMatters: args.whyItMatters ?? '',
        implications: args.implications ?? '',
        category: args.category ?? '',
        maturity: args.maturity ?? '',
        confidence: args.confidence ?? '',
        changeNote: args.changeNote ?? '',
        authoredBy: ''
    };
}
/** Creates a human-authored, accepted theme in one shot — GP-01's manual path. */ export function useCreateCustomerTheme(workspaceCorrelationId) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (args)=>{
            return projectDataClient.createCustomerTheme({
                captureProjectId: args.captureProjectId,
                originSource: args.originSource ?? 'manual',
                version: versionInput(args)
            }, correlationHeader(workspaceCorrelationId));
        },
        onSuccess: (_resp, vars)=>{
            queryClient.invalidateQueries({
                queryKey: [
                    CUSTOMER_THEMES_KEY,
                    vars.captureProjectId
                ]
            });
        },
        onError: (err)=>{
            toast.error(err instanceof Error ? err.message : 'Failed to create customer theme');
        }
    });
}
/** Creates a theme in record_state "suggested" — never produces an accepted theme. */ export function useSuggestCustomerTheme(workspaceCorrelationId) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (args)=>{
            return projectDataClient.suggestCustomerTheme({
                captureProjectId: args.captureProjectId,
                originSource: args.originSource ?? 'manual',
                proposedRevisionOfId: args.proposedRevisionOfId ?? '',
                authorship: args.authorship,
                version: versionInput(args)
            }, correlationHeader(workspaceCorrelationId));
        },
        onSuccess: (_resp, vars)=>{
            queryClient.invalidateQueries({
                queryKey: [
                    CUSTOMER_THEMES_KEY,
                    vars.captureProjectId
                ]
            });
        },
        onError: (err)=>{
            toast.error(err instanceof Error ? err.message : 'Failed to suggest customer theme');
        }
    });
}
/** Appends a human-authored version and marks the theme accepted — the only path in. */ export function useAcceptCustomerThemeSuggestion(workspaceCorrelationId) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (args)=>{
            return projectDataClient.acceptCustomerThemeSuggestion({
                captureProjectId: args.captureProjectId,
                themeId: args.themeId,
                version: versionInput(args)
            }, correlationHeader(workspaceCorrelationId));
        },
        onSuccess: (_resp, vars)=>{
            queryClient.invalidateQueries({
                queryKey: [
                    CUSTOMER_THEMES_KEY,
                    vars.captureProjectId
                ]
            });
            queryClient.invalidateQueries({
                queryKey: [
                    CUSTOMER_THEME_KEY,
                    vars.captureProjectId,
                    vars.themeId
                ]
            });
        },
        onError: (err)=>{
            toast.error(err instanceof Error ? err.message : 'Failed to accept customer theme suggestion');
        }
    });
}
/** Appends a human-authored version for an already-accepted theme. */ export function useReviseCustomerTheme(workspaceCorrelationId) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (args)=>{
            return projectDataClient.reviseCustomerTheme({
                captureProjectId: args.captureProjectId,
                themeId: args.themeId,
                version: versionInput(args)
            }, correlationHeader(workspaceCorrelationId));
        },
        onSuccess: (_resp, vars)=>{
            queryClient.invalidateQueries({
                queryKey: [
                    CUSTOMER_THEMES_KEY,
                    vars.captureProjectId
                ]
            });
            queryClient.invalidateQueries({
                queryKey: [
                    CUSTOMER_THEME_KEY,
                    vars.captureProjectId,
                    vars.themeId
                ]
            });
        },
        onError: (err)=>{
            toast.error(err instanceof Error ? err.message : 'Failed to revise customer theme');
        }
    });
}
const STAKEHOLDER_ASSESSMENTS_KEY = 'stakeholder-assessments';
/** A stakeholder's assessment history — newest first is applied by the caller. */ export function useStakeholderAssessments(captureProjectId, stakeholderId) {
    return useQuery({
        queryKey: [
            STAKEHOLDER_ASSESSMENTS_KEY,
            captureProjectId,
            stakeholderId
        ],
        enabled: !!captureProjectId && !!stakeholderId,
        queryFn: async ()=>{
            const resp = await projectDataClient.listStakeholderAssessments({
                captureProjectId,
                stakeholderId
            });
            return resp.assessments;
        }
    });
}
/** Creates or updates a stakeholder identity row — no list/get RPC exists on
 *  the browser API, so callers must hold onto the returned view themselves. */ export function useUpsertPursuitStakeholder(workspaceCorrelationId) {
    return useMutation({
        mutationFn: async (args)=>{
            return projectDataClient.upsertPursuitStakeholder({
                id: args.id ?? '',
                captureProjectId: args.captureProjectId,
                contactId: args.contactId ?? '',
                unresolvedName: args.unresolvedName,
                unresolvedOrganization: args.unresolvedOrganization ?? '',
                affiliation: args.affiliation,
                pursuitRole: args.pursuitRole ?? ''
            }, correlationHeader(workspaceCorrelationId));
        },
        onError: (err)=>{
            toast.error(err instanceof Error ? err.message : 'Failed to save stakeholder');
        }
    });
}
/** Inserts a new assessment row for a stakeholder — there is no update, only revision by new row. */ export function useRecordStakeholderAssessment(workspaceCorrelationId) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (args)=>{
            return projectDataClient.recordStakeholderAssessment({
                captureProjectId: args.captureProjectId,
                stakeholderId: args.stakeholderId,
                influenceBand: args.influenceBand,
                relationshipPosture: args.relationshipPosture ?? '',
                priorities: args.priorities ?? '',
                rationale: args.rationale,
                uncertaintyNote: args.uncertaintyNote ?? '',
                confidence: args.confidence ?? '',
                sourceKind: args.sourceKind ?? '',
                sourceRef: args.sourceRef ?? '',
                authorship: args.authorship ?? '',
                assessmentState: args.assessmentState ?? '',
                asOfDate: toTs(args.asOfDate)
            }, correlationHeader(workspaceCorrelationId));
        },
        onSuccess: (_resp, vars)=>{
            queryClient.invalidateQueries({
                queryKey: [
                    STAKEHOLDER_ASSESSMENTS_KEY,
                    vars.captureProjectId,
                    vars.stakeholderId
                ]
            });
        },
        onError: (err)=>{
            toast.error(err instanceof Error ? err.message : 'Failed to record stakeholder assessment');
        }
    });
}
/** Appends one evidence row — there is no update or delete for evidence. */ export function useAttachCustomerThemeEvidence(workspaceCorrelationId) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (args)=>{
            return projectDataClient.attachCustomerThemeEvidence({
                captureProjectId: args.captureProjectId,
                themeId: args.themeId,
                stance: args.stance,
                assertionKind: args.assertionKind,
                sourceKind: args.sourceKind,
                sourceRef: args.sourceRef ?? '',
                sourceExcerpt: args.sourceExcerpt ?? '',
                sourceDate: toTs(args.sourceDate),
                confidence: args.confidence,
                rationale: args.rationale ?? '',
                supersedesEvidenceId: args.supersedesEvidenceId ?? ''
            }, correlationHeader(workspaceCorrelationId));
        },
        onSuccess: (_resp, vars)=>{
            queryClient.invalidateQueries({
                queryKey: [
                    CUSTOMER_THEME_KEY,
                    vars.captureProjectId,
                    vars.themeId
                ]
            });
        },
        onError: (err)=>{
            toast.error(err instanceof Error ? err.message : 'Failed to attach evidence');
        }
    });
}
